import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { ButtonSpinner } from '../../components/ui/Loader';
import { Proxy5gPackagesManager } from '../../components/admin/Proxy5gPackagesManager';
import { BotButtonStudio } from '../../components/admin/BotButtonStudio';
import { useTenantPlanFeatures } from '../../hooks/useTenantPlanFeatures';
import { IS_TENANT_MODE } from '../../config/tenantMode';
import {
  useGetServerLabelsQuery,
  useUpdateServerLabelsMutation,
  useGetPaymentVisibilityQuery,
  useUpdatePaymentVisibilityMutation,
  useGetBotLanguagesQuery,
  useUpdateBotLanguagesMutation,
  useGetDisabledServersQuery,
  useToggleServerDisabledMutation,
  ServerLabelEntry,
  PaymentVisibilityEntry,
  BotButtonLang,
} from '../../store/api/adminApi';

const PAYMENT_LABELS: Record<string, string> = {
  syriatelCash: 'سيرياتيل كاش',
  shamCash: 'شام كاش',
  payeer: 'Payeer',
  crypto: 'كريبتو (Cryptomus)',
  oxapay: 'OxaPay',
  binance: 'بينانس',
  coinex: 'CoinEx',
};

/** Tenant merchants: Payeer + CoinEx + Cryptomus permanently hidden */
const HIDDEN_TENANT_PAYMENTS = new Set(['payeer', 'coinex', 'crypto']);

const LANG_META: { code: BotButtonLang; label: string }[] = [
  { code: 'ar', label: 'العربية' },
  { code: 'en', label: 'English' },
  { code: 'yo', label: 'Yorùbá' },
  { code: 'ha', label: 'Hausa' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'es', label: 'Español' },
  { code: 'bn', label: 'বাংলা' },
];

function ServerGroup({
  title,
  servers,
  labels,
  onChange,
  disabledIds,
  togglingId,
  onToggleHidden,
}: {
  title: string;
  servers: ServerLabelEntry[];
  labels: Record<string, string>;
  onChange: (id: string, value: string) => void;
  disabledIds: string[];
  togglingId: string | null;
  onToggleHidden: (id: string, hide: boolean, label: string) => void;
}) {
  return (
    <div className="mb-6">
      <h3 className="text-lg font-heading text-brand-accent mb-3">{title}</h3>
      <div className="space-y-3">
        {servers.map((s) => {
          const isHidden = disabledIds.includes(s.id);
          const busy = togglingId === s.id;
          const shownName = (labels[s.id] || '').trim() || s.defaultNameAr;
          return (
            <div
              key={s.id}
              className={`flex flex-col gap-3 p-3 rounded-lg border ${
                isHidden
                  ? 'bg-rose-500/[0.06] border-rose-500/25'
                  : 'bg-brand-primary/10 border-neutral-dark/30'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="sm:w-1/3">
                  <span className="text-sm text-neutral-medium">{s.defaultNameAr}</span>
                  {isHidden ? (
                    <span className="block text-[11px] text-rose-400 mt-0.5">مخفي من البوت والموقع</span>
                  ) : (
                    <span className="block text-[11px] text-emerald-400/90 mt-0.5">ظاهر للزبون</span>
                  )}
                </div>
                <input
                  value={labels[s.id] ?? ''}
                  onChange={(e) => onChange(s.id, e.target.value)}
                  placeholder={s.defaultNameAr}
                  className="flex-1 bg-brand-primary/20 border border-neutral-dark/40 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="flex items-center justify-between gap-3 pt-1 border-t border-white/[0.06]">
                <p className="text-xs text-neutral-medium font-body">
                  إخفاء السيرفر يمنعه من الظهور في البوت وقائمة الشراء بالموقع
                </p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onToggleHidden(s.id, !isHidden, shownName)}
                  className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-heading font-semibold transition-colors ${
                    isHidden
                      ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'
                      : 'bg-rose-500/15 text-rose-400 hover:bg-rose-500/25'
                  }`}
                >
                  {busy ? '…' : isHidden ? 'إظهار' : 'إخفاء'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AdminStoreSettingsPage() {
  const { hasNumbers, hasProxies } = useTenantPlanFeatures();
  const { data: labelsData, isLoading: labelsLoading } = useGetServerLabelsQuery();
  const { data: visibilityData, isLoading: visLoading } = useGetPaymentVisibilityQuery();
  const { data: botLangsData, isLoading: botLangsLoading } = useGetBotLanguagesQuery();
  const [updateLabels, { isLoading: savingLabels }] = useUpdateServerLabelsMutation();
  const [updateVisibility, { isLoading: savingVis }] = useUpdatePaymentVisibilityMutation();
  const [updateBotLangs, { isLoading: savingBotLangs }] = useUpdateBotLanguagesMutation();
  const { data: disabledServersData } = useGetDisabledServersQuery();
  const [toggleServerDisabled, { isLoading: isTogglingServer }] = useToggleServerDisabledMutation();
  const [togglingServerId, setTogglingServerId] = useState<string | null>(null);


  const [serverLabels, setServerLabels] = useState<Record<string, string>>({});
  const [paymentVis, setPaymentVis] = useState<Record<string, PaymentVisibilityEntry>>({});
  const [enabledBotLangs, setEnabledBotLangs] = useState<BotButtonLang[]>([]);
  const disabledIds = disabledServersData?.data?.disabledServers || [];

  useEffect(() => {
    if (labelsData?.data) {
      const all = [
        ...(hasProxies ? labelsData.data.proxies : []),
        ...(hasNumbers ? labelsData.data.tempNumbers : []),
        ...(hasNumbers ? labelsData.data.permNumbers : []),
      ];
      const map: Record<string, string> = {};
      all.forEach((s) => { map[s.id] = s.displayName || ''; });
      setServerLabels(map);
    }
  }, [labelsData, hasNumbers, hasProxies]);

  useEffect(() => {
    if (visibilityData?.data) setPaymentVis(visibilityData.data);
  }, [visibilityData]);

  useEffect(() => {
    if (botLangsData?.data?.enabled) {
      setEnabledBotLangs(botLangsData.data.enabled);
    }
  }, [botLangsData]);

  const handleToggleHidden = async (id: string, hide: boolean, label: string) => {
    setTogglingServerId(id);
    try {
      await toggleServerDisabled({ serverId: id, data: { disabled: hide } }).unwrap();
      toast.success(hide ? `تم إخفاء «${label}» من البوت` : `تم إظهار «${label}» في البوت`);
    } catch (err) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'فشل تحديث إظهار السيرفر');
    } finally {
      setTogglingServerId(null);
    }
  };

  const handleSaveLabels = async () => {
    try {
      await updateLabels(serverLabels).unwrap();
      toast.success('تم حفظ أسماء السيرفرات');
    } catch (err) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'فشل الحفظ');
    }
  };

  const handleSaveVisibility = async () => {
    try {
      await updateVisibility(paymentVis).unwrap();
      toast.success('تم حفظ إعدادات طرق الدفع');
    } catch (err) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'فشل الحفظ');
    }
  };

  const handleSaveBotLangs = async () => {
    if (!enabledBotLangs.length) {
      toast.error('لازم تضل لغة واحدة على الأقل مفعّلة');
      return;
    }
    try {
      await updateBotLangs({ enabled: enabledBotLangs }).unwrap();
      toast.success('تم حفظ لغات البوت');
    } catch (err) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'فشل الحفظ');
    }
  };

  const toggleBotLang = (code: BotButtonLang) => {
    setEnabledBotLangs((prev) => {
      if (prev.includes(code)) {
        if (prev.length <= 1) {
          toast.error('لازم تضل لغة واحدة على الأقل');
          return prev;
        }
        return prev.filter((c) => c !== code);
      }
      return [...prev, code];
    });
  };

  const togglePayment = (id: string, channel: 'bot' | 'website') => {
    setPaymentVis((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [channel]: !prev[id]?.[channel],
      },
    }));
  };

  if (labelsLoading || visLoading || botLangsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ButtonSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen animate-slide-up max-w-4xl mx-auto px-4 pb-12">
      <div className="mb-8 text-center">
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-brand-accent mb-2">
          إعدادات المتجر
        </h1>
        <p className="text-neutral-medium">
          سمِّ السيرفرات، أخفِ أي سيرفر من البوت حسب خطتك، وعدّل باقات بروكسي 4 وأزرار البوت وطرق الدفع
        </p>
      </div>

      <div className="card-glass rounded-xl p-6 mb-8">
        <h2 className="text-xl font-heading text-brand-accent mb-2">أسماء السيرفرات وإخفاؤها</h2>
        <p className="text-sm text-neutral-medium mb-4">
          سمِّ السيرفر كما يظهر للزبون، أو اخفه بالكامل من البوت والموقع. تظهر فقط السيرفرات ضمن خطتك (بروكسيات / أرقام / شاملة).
        </p>

        {labelsData?.data && (
          <>
            {hasProxies && (
            <ServerGroup
              title="بروكسيات"
              servers={labelsData.data.proxies}
              labels={serverLabels}
              onChange={(id, v) => setServerLabels({ ...serverLabels, [id]: v })}
              disabledIds={disabledIds}
              togglingId={isTogglingServer ? togglingServerId : null}
              onToggleHidden={handleToggleHidden}
            />
            )}
            {hasNumbers && (
            <>
            <ServerGroup
              title="أرقام مؤقتة"
              servers={labelsData.data.tempNumbers}
              labels={serverLabels}
              onChange={(id, v) => setServerLabels({ ...serverLabels, [id]: v })}
              disabledIds={disabledIds}
              togglingId={isTogglingServer ? togglingServerId : null}
              onToggleHidden={handleToggleHidden}
            />
            <ServerGroup
              title="أرقام دائمة"
              servers={labelsData.data.permNumbers}
              labels={serverLabels}
              onChange={(id, v) => setServerLabels({ ...serverLabels, [id]: v })}
              disabledIds={disabledIds}
              togglingId={isTogglingServer ? togglingServerId : null}
              onToggleHidden={handleToggleHidden}
            />
            </>
            )}
          </>
        )}

        <button
          onClick={handleSaveLabels}
          disabled={savingLabels}
          className="btn-primary px-6 py-2 rounded-lg font-body"
          type="button"
        >
          {savingLabels ? 'جاري الحفظ…' : 'حفظ أسماء السيرفرات'}
        </button>
      </div>

      {hasProxies && <Proxy5gPackagesManager />}

      <BotButtonStudio />

      <div className="card-glass rounded-xl p-6 mb-8">
        <h2 className="text-xl font-heading text-brand-accent mb-2">لغات البوت</h2>
        <p className="text-sm text-neutral-medium mb-4">
          اختر اللغات اللي تظهر للزبون لما يضغط زر اللغة بالبوت. لازم تضل لغة واحدة مفعّلة على الأقل.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {LANG_META.map((l) => (
            <label
              key={l.code}
              className="flex items-center justify-between gap-3 p-3 rounded-lg bg-brand-primary/10 border border-neutral-dark/30 cursor-pointer"
            >
              <span className="font-body">{l.label}</span>
              <input
                type="checkbox"
                checked={enabledBotLangs.includes(l.code)}
                onChange={() => toggleBotLang(l.code)}
              />
            </label>
          ))}
        </div>
        <button
          onClick={handleSaveBotLangs}
          disabled={savingBotLangs}
          className="btn-primary px-6 py-2 rounded-lg font-body"
        >
          {savingBotLangs ? 'جاري الحفظ...' : 'حفظ لغات البوت'}
        </button>
      </div>

      <div className="card-glass rounded-xl p-6">
        <h2 className="text-xl font-heading text-brand-accent mb-4">ظهور طرق الدفع</h2>
        <div className="space-y-3 mb-4">
          {Object.keys(PAYMENT_LABELS)
            .filter((id) => !(IS_TENANT_MODE && HIDDEN_TENANT_PAYMENTS.has(id)))
            .map((id) => (
            <div key={id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg bg-brand-primary/10 border border-neutral-dark/30">
              <span className="font-body">{PAYMENT_LABELS[id]}</span>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentVis[id]?.bot !== false}
                    onChange={() => togglePayment(id, 'bot')}
                  />
                  بوت
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentVis[id]?.website !== false}
                    onChange={() => togglePayment(id, 'website')}
                  />
                  موقع
                </label>
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={handleSaveVisibility}
          disabled={savingVis}
          className="btn-primary px-6 py-2 rounded-lg font-body"
        >
          {savingVis ? 'جاري الحفظ...' : 'حفظ طرق الدفع'}
        </button>
      </div>
    </div>
  );
}
